import { Product, ComboData } from "../../stores/appReportsStore";
import { Platform } from "../../types/Platforms";
import { getAccessToken } from "../authentication/supabaseService";
import { RawProductResponse } from "../import/importProductIdsService";


export async function searchProducts(
    ingredients: string[],
    restaurantId: string,
    parentCategory?: string,
): Promise<Record<string, Product[]>> {
    const token = await getAccessToken();
    const res = await fetch('/api/searchProducts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ ingredients, restaurantId, parentCategory }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to search products');
    }
  
    const { lists: raw }: { lists: Record<string, RawProductResponse[]> } =
      await res.json();

    console.log('SEARCH DATA RAW: ', raw)
  
    const mapped: Record<string, Product[]> = {};
    for (const id of ingredients) {
      const list = raw[id] ?? [];
      mapped[id] = list.map(mapRawToProduct);
    }
    return mapped;
}


function mapRawToProduct(raw: RawProductResponse): Product {
    const combo: ComboData | undefined = raw.hasComboOption
        ? {
            price: raw.comboPrice!,
            multiplier: raw.comboMultiplier!,
            standardizedAmount: raw.quantityInfo.combo!.standardizedAmount,
        }
        : undefined;

    return {
        platform: Platform.fromString(raw.platform),
        category: raw.category,
        name: raw.name,
        brand: raw.brand,
        articleId: raw.articleId,
        src: raw.src,
        link: raw.link,
        matchScore: raw.matchScore,
        price: raw.price,
        isKiloPrice: raw.isKiloPrice,
        unitPrice: raw.unitPrice,
        unitPriceUnit: raw.unitPriceUnit,
        quantity: raw.quantity,
        useSingle: true,
        useCombo: false,
        standardUnit: raw.quantityInfo.single.standardUnit,
        standardizedAmount: raw.quantityInfo.single.standardizedAmount,
        estimatedWeightPerPiece: raw.quantityInfo.single.estimatedWeightPerPiece,
        hasComboOption: raw.hasComboOption,
        combo,
    };
}


export interface RecalcSearchResult {
    newOptions: Product[]
    newSelected: Product
  }

  export function recalcSearchQuantities(
    selected: Product,
    options: Product[]
  ): RecalcSearchResult {
    // 1) compute required standardized total
    const requiredStd =
      selected.quantity *
      (selected.useSingle
        ? selected.standardizedAmount
        : selected.combo?.standardizedAmount ?? 0);
  
    // 2) build new options list
    const newOptions = options.map(opt => {
      let qty: number;
  
      if (opt.standardUnit === "piece" || opt.standardUnit === "stuk") {
        if (opt.estimatedWeightPerPiece && opt.estimatedWeightPerPiece > 0) {
          // estimatedWeightPerPiece is in grams per piece
          qty = Math.max(1, Math.ceil(requiredStd / opt.estimatedWeightPerPiece));
        } else {
          qty = selected.quantity;
        }
      } else {
        const optStd =
          opt.useSingle
            ? opt.standardizedAmount
            : opt.combo?.standardizedAmount ?? 0;
        // always round up to next whole unit
        qty = Math.max(1, Math.ceil(requiredStd / optStd));
      }
  
      return { ...opt, quantity: qty };
    });
  
    // 3) re-pick the selected item (by articleId), fallback to first
    const found = newOptions.find(o => o.articleId === selected.articleId);
    const newSelected = found
      ? { ...found }
      : { ...newOptions[0] };
  
    return { newOptions, newSelected };
  }